class AbrioContactForm extends HTMLElement {
    constructor() {
        super();

        this.maxFileSizeBytes = 10 * 1024 * 1024;
        this.form = this.querySelector('form');
        this.submitButton = this.form?.querySelector('button[type="submit"]');
        this.fileUpload = this.querySelectorAll('input[type=file]');
        this.hasFileSizeError = false;
    }

    connectedCallback() {
        if(this.form) {
            this.form.addEventListener('submit',
                this.#onSubmit.bind(this)
            );
        }

        if(this.fileUpload) {
            this.fileUpload.forEach((input) => {
                input.addEventListener('change', (event) => {
                    this.filesArray = this.filesArray || [];
                    const inputElement = /** @type {HTMLInputElement} */ (event.target);
                    if (!inputElement?.files) return;
                    
                    const files = Array.from(inputElement.files);

                    this.hasFileSizeError = false;
                    this.#setFileErrorState(inputElement, true);

                    files.forEach((file) => {
                        const invalidFileSize = file.size > this.maxFileSizeBytes;

                        if (invalidFileSize) {
                            this.hasFileSizeError = true;
                            this.#setFileErrorState(inputElement, false);
                            return;
                        }

                        this.filesArray.push(file);
                        this.#addUploadedFile(file);
                    });

                    inputElement.value = '';
                });
            });
        }
    }

    /**
     * @param {SubmitEvent} event
     */
    #onSubmit(event) {
        event.preventDefault();

        const hasOversizedFile = this.#checkForFileSizeErrors();
        const isValid = this.#checkForFieldErrors();

        if(!isValid || hasOversizedFile) return;

        const formData = this.#createFormData(event);

        fetch("/apps/abrio-homepage/contact-form", {
            method: "POST",
            body: formData,
        })
        .then((response) => {
            if(response.status !== 200) {
                throw new Error(`Error submitting form. Status: ${response.status}`);
            }
            this.form?.classList?.add('success');
        })
        .catch((error) => {
            console.error('Error submitting form:', error);
        })
    }

    /**
     * @param {SubmitEvent} event
     */
    #createFormData(event) {
        // @ts-ignore
        const formData = new FormData(event.target);

        /** @type {Record<string, any>} */
        const data = {};

        for (let [key, value] of formData.entries()) {
            // Match keys like contact[name]
            const match = key.match(/^(\w+)\[(\w+)\]$/);

            if (match) {
                const objName = match[1]; // "contact"
                const propName = match[2]; // "name", "email", "body"

                if (!objName || !propName) continue;

                if (!data[objName]) data[objName] = {};
                data[objName][propName] = value;

                continue;
            }

            // plain fields without brackets
            data[key] = value;
        }

        const finalFormData = new FormData();
        const body = {
            // Nodemailer configuration
            ...data.nodemailer,

            // Email Dto
            ...data.contact
        }
        finalFormData.append("requestBody", JSON.stringify(body));

        // Add files to FormData
        if(this.filesArray){
            Array.from(this.filesArray).forEach((file)=> {
                const fileBlob = new Blob([file], {type: file.type});
                finalFormData.append('files[]', fileBlob, file.name);
            })
        }

        return finalFormData;
    }

    /**
     * Adds an uploaded file to the uploaded files list.
     * @param {File} file
     */
    #addUploadedFile(file){
      /** @type {HTMLElement | null} */
      const parent = this.querySelector('.uploaded-files');
      if(parent){
        const wrapper = document.createElement('div');
        wrapper.classList.add('uploaded-file');

        const p = document.createElement('p');
        p.textContent = file.name;

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.classList.add('uploaded-file__remove');
        removeButton.setAttribute('aria-label', `Remove ${file.name}`);
        removeButton.innerHTML = '&times;';
        removeButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const index = this.filesArray.indexOf(file);
          if (index > -1) {
            this.filesArray.splice(index, 1);
          }
          wrapper.remove();
        });

        wrapper.appendChild(p);
        wrapper.appendChild(removeButton);
        parent.appendChild(wrapper);
      }
    }

    /**
     * Checks for field errors and add error classes to fields that are not filled
     */
    #checkForFieldErrors() {
        if(!this.form) return;
        
        let isValid = true;
        const fields = [
            { name: 'contact[first_name]', element: this.form.querySelector('input[name="contact[first_name]"]') },
            { name: 'contact[last_name]', element: this.form.querySelector('input[name="contact[last_name]"]') },
            { name: 'contact[email]', element: this.form.querySelector('input[name="contact[email]"]') },
            { name: 'contact[message]', element: this.form.querySelector('textarea[name="contact[message]"]') },
        ];

        fields.forEach(field => {
            const inputElement = /** @type {HTMLInputElement | HTMLTextAreaElement} */ (field.element);
            if (!inputElement) return;

            // Special email validation
            if (inputElement.type === "email") {
                const emailValue = inputElement.value.trim();
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                if (!emailRegex.test(emailValue)) {
                    this.#setErrorState(inputElement, false); // invalid email
                    isValid = false;
                    return; // skip further checks
                }
            }

            // Generic empty-field validation
            if (!inputElement.value) {
                this.#setErrorState(inputElement, false);
                isValid = false;
                return;
            }

            this.#setErrorState(inputElement, true);
        });

        return isValid;
    }

    /**
     * Checks all selected files for file size violations.
     * @returns {boolean}
     */
    #checkForFileSizeErrors() {
        this.fileUpload.forEach((input) => {
            const inputElement = /** @type {HTMLInputElement} */ (input);
            if (this.hasFileSizeError) {
                this.#setFileErrorState(inputElement, false);
                return;
            }

            this.#setFileErrorState(inputElement, true);
        });

        return this.hasFileSizeError;
    }

    /**
     * Toggles file input error state (same pattern as #setErrorState).
     * @param {HTMLInputElement} input
     * @param {boolean} isValid
     */
    #setFileErrorState(input, isValid) {
        if (!input) return;

        const field = input.closest('.input-wrapper');
        const errorElement = input.id ? this.form?.querySelector(`#${input.id}-error`) : null;
        const defaultDescribedBy = input.getAttribute('data-default-describedby') || '';
        const describedByTokens = defaultDescribedBy
            .split(' ')
            .map((token) => token.trim())
            .filter(Boolean);

        if (!isValid) {
            field?.classList.add('error');
            input.setAttribute('aria-invalid', 'true');
            if (input.id) {
                describedByTokens.push(`${input.id}-error`);
            }
            input.setAttribute('aria-describedby', Array.from(new Set(describedByTokens)).join(' '));
            if (errorElement) {
                errorElement.removeAttribute('hidden');
            }
            return;
        }

        field?.classList.remove('error');
        input.removeAttribute('aria-invalid');
        if (describedByTokens.length > 0) {
            input.setAttribute('aria-describedby', Array.from(new Set(describedByTokens)).join(' '));
        } else {
            input.removeAttribute('aria-describedby');
        }
        if (errorElement) {
            errorElement.setAttribute('hidden', 'hidden');
        }
    }

    /**
     * Sets error state on inputs according to its validity
     * @param {HTMLInputElement | HTMLTextAreaElement} input
     * @param {boolean} isValid
     */
    #setErrorState(input, isValid) {
        if(!input) return;

        const field = input.closest(".input-wrapper");
        const errorElement = input.id ? this.form?.querySelector(`#${input.id}-error`) : null;
        const defaultDescribedBy = input.getAttribute('data-default-describedby') || '';
        const describedByTokens = defaultDescribedBy
            .split(' ')
            .map((token) => token.trim())
            .filter(Boolean);

        if(!isValid) {
            field?.classList.add('error');
            input.setAttribute('aria-invalid', 'true');
            if (input.id) {
                describedByTokens.push(`${input.id}-error`);
            }
            input.setAttribute('aria-describedby', Array.from(new Set(describedByTokens)).join(' '));
            if (errorElement) {
                errorElement.removeAttribute('hidden');
            }
            return;
        }

        field?.classList.remove('error');
        input.removeAttribute('aria-invalid');
        if (describedByTokens.length > 0) {
            input.setAttribute('aria-describedby', Array.from(new Set(describedByTokens)).join(' '));
        } else {
            input.removeAttribute('aria-describedby');
        }
        if (errorElement) {
            errorElement.setAttribute('hidden', 'hidden');
        }
    }
}

if (!customElements.get('abrio-contact-form')) customElements.define("abrio-contact-form", AbrioContactForm);
